import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';
import { formatXPUNT, formatDate } from '../utils/helpers';
import { getExchangeRate, toXPUNT, fromXPUNT } from '../utils/exchangeRate';
import { CURRENCIES } from '../utils/constants';
import { 
  FaWallet, FaArrowUp, FaArrowDown, FaHistory, 
  FaCreditCard, FaBitcoin, FaExchangeAlt,
  FaPlus, FaMinus, FaCopy, FaCheck,
  FaSpinner, FaSync, FaDollarSign,
  FaMobile, FaUniversity, FaMoneyBillWave,
  FaCheckCircle, FaTimesCircle, FaClock,
  FaShieldAlt, FaCrypto
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './Wallet.css';

const Wallet = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    balance, totalWinnings, loading, transactions,
    fundWallet, initializePayment, verifyPayment,
    withdraw, cryptoBalance, cryptoAddress,
    fetchBalance, fetchTransactions, fetchCryptoBalance,
    createCryptoWallet, processCryptoDeposit
  } = useWallet();
  
  const [activeTab, setActiveTab] = useState('balance');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [convertedXPUNT, setConvertedXPUNT] = useState(null);
  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [flutterwaveReady, setFlutterwaveReady] = useState(false);
  const [cryptoDepositAmount, setCryptoDepositAmount] = useState('');
  const [cryptoDepositTx, setCryptoDepositTx] = useState('');
  const [cryptoDepositFrom, setCryptoDepositFrom] = useState('');
  const [showCryptoDepositForm, setShowCryptoDepositForm] = useState(false);
  const [convertedCryptoXPUNT, setConvertedCryptoXPUNT] = useState(null);

  // Get user's fixed currency from registration
  const userCurrency = user?.local_currency || 'NGN';
  const currencySymbol = user?.currency_symbol || CURRENCIES[userCurrency]?.symbol || '₦';

  // Load Flutterwave script
  useEffect(() => {
    const loadFlutterwaveScript = () => {
      if (document.querySelector('script[src="https://checkout.flutterwave.com/v3.js"]')) {
        setFlutterwaveReady(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.flutterwave.com/v3.js';
      script.async = true;
      script.onload = () => {
        setFlutterwaveReady(true);
        console.log('Flutterwave script loaded');
      };
      script.onerror = () => {
        console.error('Failed to load Flutterwave script');
        toast.error('Payment service unavailable. Please try again later.');
      };
      document.body.appendChild(script);
    };

    loadFlutterwaveScript();
  }, []);

  // Fetch exchange rate for user's fixed currency
  useEffect(() => {
    fetchExchangeRate();
  }, [userCurrency]);

  const fetchExchangeRate = async () => {
    setExchangeLoading(true);
    try {
      // Get rate for user's currency to USD
      const rate = await getExchangeRate(userCurrency);
      setExchangeRate(rate);
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      setExchangeRate(1500); // Fallback for NGN
    } finally {
      setExchangeLoading(false);
    }
  };

  // Update converted amount when deposit amount changes
  useEffect(() => {
    if (depositAmount && exchangeRate) {
      const amount = parseFloat(depositAmount);
      if (!isNaN(amount) && amount > 0) {
        // Convert local currency to $XPUNT (1 $XPUNT = 1 USD)
        // amount / exchangeRate = USD amount = $XPUNT amount
        const xpuntAmount = amount / exchangeRate;
        setConvertedXPUNT(xpuntAmount);
      } else {
        setConvertedXPUNT(null);
      }
    } else {
      setConvertedXPUNT(null);
    }
  }, [depositAmount, exchangeRate]);

  // Update crypto converted amount (USDC to $XPUNT)
  useEffect(() => {
    if (cryptoDepositAmount) {
      const amount = parseFloat(cryptoDepositAmount);
      if (!isNaN(amount) && amount > 0) {
        // USDC is pegged to USD, so 1 USDC = 1 $XPUNT
        setConvertedCryptoXPUNT(amount);
      } else {
        setConvertedCryptoXPUNT(null);
      }
    } else {
      setConvertedCryptoXPUNT(null);
    }
  }, [cryptoDepositAmount]);

  const handleFlutterwavePayment = async () => {
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    // Calculate $XPUNT amount (1 $XPUNT = 1 USD)
    const xpuntAmount = amount / exchangeRate;
    if (xpuntAmount <= 0) {
      toast.error('Amount too small');
      return;
    }

    if (!flutterwaveReady) {
      toast.error('Payment service is loading. Please try again.');
      return;
    }

    setIsProcessing(true);
    setPaymentStatus('initializing');

    try {
      // Initialize payment - user pays in local currency, receives $XPUNT
      const response = await initializePayment(xpuntAmount, userCurrency);
      
      if (!response.success) {
        throw new Error(response.error || 'Payment initialization failed');
      }

      const Flutterwave = window.FlutterwaveCheckout;
      
      Flutterwave({
        public_key: process.env.REACT_APP_FLUTTERWAVE_PUBLIC_KEY,
        tx_ref: response.tx_ref,
        amount: amount, // User pays in their local currency
        currency: userCurrency, // User's local currency
        payment_options: 'card, banktransfer, ussd, mobilemoney',
        redirect_url: `${window.location.origin}/payment/callback`,
        meta: {
          user_id: user?.id,
          xpunt_amount: xpuntAmount,
          local_currency: userCurrency,
          local_amount: amount,
          exchange_rate: exchangeRate,
        },
        customer: {
          email: user?.email || 'customer@example.com',
          name: user?.display_name || 'XPunters User',
        },
        customizations: {
          title: 'XPunters Wallet Deposit',
          description: `You will receive ${xpuntAmount.toFixed(2)} $XPUNT (1 $XPUNT = 1 USD)`,
          logo: `${window.location.origin}/logo.png`,
        },
        callback: async (response) => {
          setPaymentStatus('processing');
          try {
            const verifyResult = await verifyPayment(response.transaction_id);
            if (verifyResult.success) {
              setPaymentStatus('success');
              toast.success(`Deposited ${formatXPUNT(xpuntAmount)} successfully!`);
              setDepositAmount('');
              setConvertedXPUNT(null);
              await fetchBalance();
              await fetchTransactions();
            } else {
              setPaymentStatus('failed');
              toast.error('Payment verification failed');
            }
          } catch (error) {
            setPaymentStatus('failed');
            toast.error('Failed to verify payment');
          }
          setPaymentStatus(null);
          setIsProcessing(false);
        },
        onclose: () => {
          if (paymentStatus !== 'success') {
            setPaymentStatus('cancelled');
            toast.error('Payment was cancelled');
          }
          setIsProcessing(false);
          setTimeout(() => setPaymentStatus(null), 3000);
        },
      });
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Payment failed. Please try again.');
      setIsProcessing(false);
      setPaymentStatus('failed');
      setTimeout(() => setPaymentStatus(null), 3000);
    }
  };

  // Manual deposit for testing
  const handleManualDeposit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(depositAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const xpuntAmount = amount / exchangeRate;
    if (xpuntAmount <= 0) {
      toast.error('Amount too small');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await fundWallet(xpuntAmount);
      if (result.success) {
        toast.success(`Deposited ${formatXPUNT(xpuntAmount)} successfully!`);
        setDepositAmount('');
        setConvertedXPUNT(null);
        await fetchBalance();
        await fetchTransactions();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCryptoDeposit = async () => {
    const amount = parseFloat(cryptoDepositAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!cryptoDepositTx || !cryptoDepositFrom) {
      toast.error('Please enter transaction ID and from address');
      return;
    }

    setIsProcessing(true);
    try {
      // USDC deposit - 1 USDC = 1 $XPUNT
      const xpuntAmount = amount;
      
      const result = await processCryptoDeposit({
        amount: amount,
        transactionId: cryptoDepositTx,
        fromAddress: cryptoDepositFrom,
        subAccountId: user?.id,
      });

      if (result.success) {
        toast.success(`Deposited ${formatXPUNT(xpuntAmount)} from USDC!`);
        setCryptoDepositAmount('');
        setCryptoDepositTx('');
        setCryptoDepositFrom('');
        setConvertedCryptoXPUNT(null);
        setShowCryptoDepositForm(false);
        await fetchBalance();
        await fetchCryptoBalance();
        await fetchTransactions();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to process crypto deposit');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCreateCryptoWallet = async () => {
    setIsProcessing(true);
    try {
      await createCryptoWallet({ coin: 'USDC', chain: 'ETH' });
      toast.success('Crypto wallet created successfully!');
      await fetchCryptoBalance();
    } catch (error) {
      toast.error(error.message || 'Failed to create crypto wallet');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (amount > balance) {
      toast.error('Insufficient balance');
      return;
    }
    if (!bankDetails.bank_name || !bankDetails.account_number || !bankDetails.account_name) {
      toast.error('Please fill in all bank details');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await withdraw(amount, bankDetails);
      if (result.success) {
        setWithdrawAmount('');
        setBankDetails({
          bank_name: '',
          account_number: '',
          account_name: '',
        });
        await fetchBalance();
        await fetchTransactions();
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const copyAddress = () => {
    if (cryptoAddress) {
      navigator.clipboard.writeText(cryptoAddress);
      setCopied(true);
      toast.success('Address copied!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const getPaymentStatusIcon = () => {
    switch (paymentStatus) {
      case 'initializing': return <FaClock className="status-icon processing" />;
      case 'processing': return <FaSpinner className="status-icon processing spin" />;
      case 'success': return <FaCheckCircle className="status-icon success" />;
      case 'failed': return <FaTimesCircle className="status-icon failed" />;
      case 'cancelled': return <FaTimesCircle className="status-icon cancelled" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="wallet-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="wallet-page">
      <div className="page-header">
        <h1>Wallet</h1>
        <p className="page-subtitle">Manage your funds and transactions</p>
        <div className="currency-display">
          <span className="currency-label">Currency:</span>
          <span className="currency-value">{currencySymbol} {userCurrency}</span>
          <span className="currency-note">(Fixed at registration)</span>
        </div>
      </div>

      {/* Balance Overview */}
      <div className="balance-overview">
        <div className="balance-card main">
          <div className="balance-label">Available Balance</div>
          <div className="balance-amount">{formatXPUNT(balance)}</div>
          {exchangeRate && (
            <div className="balance-conversion">
              ≈ {currencySymbol}{(balance * exchangeRate).toFixed(2)} {userCurrency}
            </div>
          )}
          <div className="balance-actions">
            <button 
              className="btn btn-primary"
              onClick={() => setActiveTab('deposit')}
            >
              <FaPlus /> Deposit
            </button>
            <button 
              className="btn btn-secondary"
              onClick={() => setActiveTab('withdraw')}
              disabled={balance <= 0}
            >
              <FaMinus /> Withdraw
            </button>
          </div>
        </div>
        <div className="balance-card secondary">
          <div className="balance-label">Total Winnings</div>
          <div className="balance-amount gold">{formatXPUNT(totalWinnings)}</div>
        </div>
        {cryptoAddress && (
          <div className="balance-card crypto">
            <div className="balance-label">💎 USDC Wallet</div>
            <div className="balance-amount">{cryptoBalance || 0} USDC</div>
            {exchangeRate && (
              <div className="balance-conversion">
                ≈ {formatXPUNT(cryptoBalance || 0)} ($XPUNT)
              </div>
            )}
            <div className="crypto-address" onClick={copyAddress}>
              <span>{cryptoAddress.slice(0, 10)}...{cryptoAddress.slice(-8)}</span>
              {copied ? <FaCheck /> : <FaCopy />}
            </div>
          </div>
        )}
      </div>

      {/* Live Exchange Rate */}
      <div className="exchange-rate-banner">
        <FaExchangeAlt className="exchange-icon" />
        <span>
          1 $XPUNT = 
          {exchangeLoading ? (
            <FaSpinner className="spinner" />
          ) : (
            <strong> {currencySymbol}{exchangeRate?.toFixed(2) || '...'} {userCurrency}</strong>
          )}
        </span>
        <span className="rate-note">(1 $XPUNT = 1 USD)</span>
        <button onClick={fetchExchangeRate} className="refresh-rate" disabled={exchangeLoading}>
          <FaSync className={exchangeLoading ? 'spin' : ''} />
        </button>
      </div>

      {/* Payment Status */}
      {paymentStatus && (
        <div className={`payment-status ${paymentStatus}`}>
          {getPaymentStatusIcon()}
          <span>
            {paymentStatus === 'initializing' && 'Initializing payment...'}
            {paymentStatus === 'processing' && 'Processing payment...'}
            {paymentStatus === 'success' && 'Payment successful! 🎉'}
            {paymentStatus === 'failed' && 'Payment failed. Please try again.'}
            {paymentStatus === 'cancelled' && 'Payment cancelled.'}
          </span>
        </div>
      )}

      {/* Tabs */}
      <div className="wallet-tabs">
        <button 
          className={`tab-btn ${activeTab === 'balance' ? 'active' : ''}`}
          onClick={() => setActiveTab('balance')}
        >
          <FaHistory /> Transactions
        </button>
        <button 
          className={`tab-btn ${activeTab === 'deposit' ? 'active' : ''}`}
          onClick={() => setActiveTab('deposit')}
        >
          <FaArrowDown /> Deposit
        </button>
        <button 
          className={`tab-btn ${activeTab === 'withdraw' ? 'active' : ''}`}
          onClick={() => setActiveTab('withdraw')}
        >
          <FaArrowUp /> Withdraw
        </button>
        <button 
          className={`tab-btn ${activeTab === 'crypto' ? 'active' : ''}`}
          onClick={() => setActiveTab('crypto')}
        >
          <FaBitcoin /> Crypto
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Transactions */}
        {activeTab === 'balance' && (
          <div className="transactions-section">
            <h3>Transaction History</h3>
            {transactions.length === 0 ? (
              <div className="empty-state">
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="transaction-list">
                {transactions.map((tx) => (
                  <div key={tx.id} className="transaction-item">
                    <div className="tx-icon">
                      {tx.type === 'deposit' ? <FaArrowDown className="green" /> :
                       tx.type === 'withdrawal' ? <FaArrowUp className="red" /> :
                       tx.type === 'crypto_deposit' ? <FaBitcoin className="blue" /> :
                       <FaExchangeAlt />}
                    </div>
                    <div className="tx-info">
                      <span className="tx-description">{tx.description}</span>
                      <span className="tx-date">{formatDate(tx.created_at)}</span>
                    </div>
                    <div className="tx-amount">
                      <span className={tx.amount > 0 ? 'positive' : 'negative'}>
                        {tx.amount > 0 ? '+' : ''}{formatXPUNT(tx.amount)}
                      </span>
                      <span className={`tx-status ${tx.status}`}>
                        {tx.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Deposit */}
        {activeTab === 'deposit' && (
          <div className="deposit-section">
            <h3>Deposit Funds</h3>
            
            <div className="deposit-methods">
              <div className="method-card flutterwave" onClick={handleFlutterwavePayment}>
                <FaCreditCard className="method-icon" />
                <span>Flutterwave</span>
                <small>Pay in {userCurrency}</small>
                <span className="method-badge">Secure</span>
              </div>
              <div className="method-card" onClick={() => setActiveTab('crypto')}>
                <FaBitcoin className="method-icon" />
                <span>Crypto</span>
                <small>USDC</small>
              </div>
            </div>

            <form className="deposit-form">
              <div className="form-group">
                <label>Amount ({userCurrency})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">{currencySymbol}</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>
                <small className="field-hint">Currency: {userCurrency} (fixed)</small>
              </div>

              {convertedXPUNT !== null && exchangeRate && (
                <div className="conversion-info">
                  <div className="conversion-row">
                    <span>You pay</span>
                    <span>{currencySymbol}{parseFloat(depositAmount).toFixed(2)} {userCurrency}</span>
                  </div>
                  <div className="conversion-row highlight">
                    <span>You receive</span>
                    <span><strong>{formatXPUNT(convertedXPUNT)}</strong></span>
                  </div>
                  <div className="conversion-row">
                    <span>Exchange Rate</span>
                    <span>1 $XPUNT = {currencySymbol}{exchangeRate.toFixed(2)} {userCurrency}</span>
                  </div>
                  <div className="conversion-row note">
                    <span>Note</span>
                    <span>1 $XPUNT = 1 USD</span>
                  </div>
                </div>
              )}

              <div className="deposit-actions">
                <button 
                  type="button"
                  className="btn btn-primary btn-lg flutterwave-btn"
                  onClick={handleFlutterwavePayment}
                  disabled={isProcessing || !depositAmount || parseFloat(depositAmount) <= 0 || !flutterwaveReady}
                >
                  {isProcessing ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <>
                      <FaCreditCard /> Pay {parseFloat(depositAmount)?.toFixed(2) || '0.00'} {userCurrency} with Flutterwave
                    </>
                  )}
                </button>
              </div>

              <div className="deposit-info">
                <div className="info-item">
                  <FaShieldAlt className="info-icon" />
                  <span>Secure payment powered by Flutterwave</span>
                </div>
                <div className="info-item">
                  <FaClock className="info-icon" />
                  <span>Funds reflect instantly after confirmation</span>
                </div>
                <div className="info-item">
                  <FaExchangeAlt className="info-icon" />
                  <span>Rate: 1 $XPUNT = {currencySymbol}{exchangeRate?.toFixed(2) || '...'} {userCurrency}</span>
                </div>
                <div className="info-item">
                  <FaDollarSign className="info-icon" />
                  <span>1 $XPUNT = 1 USD</span>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Withdraw */}
        {activeTab === 'withdraw' && (
          <div className="withdraw-section">
            <h3>Withdraw Funds</h3>
            <div className="withdraw-info">
              <p>Minimum withdrawal: 10 $XPUNT</p>
              <p>Available: {formatXPUNT(balance)}</p>
              {exchangeRate && (
                <p>≈ {currencySymbol}{(balance * exchangeRate).toFixed(2)} {userCurrency}</p>
              )}
            </div>
            <form onSubmit={handleWithdraw} className="withdraw-form">
              <div className="form-group">
                <label>Amount ($XPUNT)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">$</span>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    min="10"
                    step="0.01"
                    required
                  />
                </div>
                {exchangeRate && withdrawAmount && parseFloat(withdrawAmount) > 0 && (
                  <small className="conversion-hint">
                    ≈ {currencySymbol}{(parseFloat(withdrawAmount) * exchangeRate).toFixed(2)} {userCurrency}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  placeholder="e.g. GTBank"
                  value={bankDetails.bank_name}
                  onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Account Number</label>
                <input
                  type="text"
                  placeholder="Enter account number"
                  value={bankDetails.account_number}
                  onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Account Name</label>
                <input
                  type="text"
                  placeholder="Enter account name"
                  value={bankDetails.account_name}
                  onChange={(e) => setBankDetails({ ...bankDetails, account_name: e.target.value })}
                  required
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary btn-lg"
                disabled={isProcessing || balance < 10}
              >
                {isProcessing ? <LoadingSpinner size="small" /> : 'Withdraw'}
              </button>
            </form>
          </div>
        )}

        {/* Crypto */}
        {activeTab === 'crypto' && (
          <div className="crypto-section">
            <h3>Crypto Wallet</h3>
            
            {/* Currency info */}
            <div className="crypto-currency-info">
              <span className="info-badge">💱 1 $XPUNT = 1 USD</span>
              <span className="info-badge">💰 1 USDC = 1 $XPUNT</span>
              {exchangeRate && (
                <span className="info-badge">
                  💱 1 $XPUNT = {currencySymbol}{exchangeRate.toFixed(2)} {userCurrency}
                </span>
              )}
            </div>

            {cryptoAddress ? (
              <div className="crypto-wallet-info">
                <div className="crypto-balance">
                  <span className="crypto-label">USDC Balance</span>
                  <span className="crypto-amount">{cryptoBalance || 0} USDC</span>
                  {exchangeRate && (
                    <span className="crypto-conversion">
                      ≈ {formatXPUNT(cryptoBalance || 0)} ($XPUNT)
                    </span>
                  )}
                </div>
                
                <div className="crypto-address-section">
                  <span className="crypto-label">Deposit Address</span>
                  <div className="crypto-address-box" onClick={copyAddress}>
                    <code>{cryptoAddress}</code>
                    {copied ? <FaCheck className="copied" /> : <FaCopy />}
                  </div>
                  <small>Send USDC on ETH network to this address</small>
                </div>

                {/* Crypto Deposit Form */}
                <div className="crypto-deposit-section">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowCryptoDepositForm(!showCryptoDepositForm)}
                  >
                    {showCryptoDepositForm ? 'Hide Deposit Form' : 'Deposit USDC'}
                  </button>

                  {showCryptoDepositForm && (
                    <div className="crypto-deposit-form">
                      <h4>Deposit USDC</h4>
                      <p className="crypto-deposit-hint">
                        Enter the USDC amount and transaction details.
                        1 USDC = 1 $XPUNT (1 $XPUNT = 1 USD)
                      </p>
                      <div className="form-group">
                        <label>USDC Amount</label>
                        <input
                          type="number"
                          placeholder="0.00"
                          value={cryptoDepositAmount}
                          onChange={(e) => setCryptoDepositAmount(e.target.value)}
                          min="0.01"
                          step="0.01"
                        />
                        {convertedCryptoXPUNT !== null && (
                          <small className="conversion-hint">
                            ≈ {formatXPUNT(convertedCryptoXPUNT)} ($XPUNT)
                          </small>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Transaction ID (TX Hash)</label>
                        <input
                          type="text"
                          placeholder="0x..."
                          value={cryptoDepositTx}
                          onChange={(e) => setCryptoDepositTx(e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>From Address</label>
                        <input
                          type="text"
                          placeholder="0x..."
                          value={cryptoDepositFrom}
                          onChange={(e) => setCryptoDepositFrom(e.target.value)}
                        />
                      </div>
                      <button 
                        className="btn btn-primary"
                        onClick={handleCryptoDeposit}
                        disabled={isProcessing}
                      >
                        {isProcessing ? <LoadingSpinner size="small" /> : 'Process Deposit'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="crypto-warning">
                  ⚠️ Only send USDC on the Ethereum network. Other networks may result in loss of funds.
                </div>
              </div>
            ) : (
              <div className="crypto-empty">
                <div className="crypto-actions">
                  <Link to="/crypto/deposit" className="btn btn-primary">
                    <FaBitcoin /> Deposit Crypto
                  </Link>
                 
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;